import { Component, OnInit, Input, ElementRef, AfterViewInit, ChangeDetectorRef, SimpleChanges, OnChanges, OnDestroy, EventEmitter, Output } from "@angular/core";

import { State, Section, Page, StateType, FlowInstance } from "../../models/flow.model";
import { EventsService } from "../../../services/events.service";
import { Subscription, Observable, forkJoin } from "rxjs";
import { tap } from "rxjs/operators";
import { ApplicationConfigService, ModuleConfig } from "../../../services/application-config.service";
import { SystemSetting, SystemSettingSearchSpec, SettingNames } from "../../../admin/models/system-setting.model";
import { SystemSettingsService } from "../../../admin/services/system-settings.service";



@Component({
    selector: "flow-sections",
    templateUrl: "./flow-sections.component.html",
    styleUrls: ["./flow-sections.component.scss"],
    standalone: false
})

export class FlowSectionsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    _availableWidth: number;
    _indicatorWidth: number;
    disablePrevious: boolean;
    disableNext: boolean;
    _trimmingIndicators: boolean = false;
    _showHowManyIndicators: number;
    _firstVisibleIndicator: number = 1;
    _aryCurrentlyVisibleIndicators: Array<number>;
    currentEditingIndicatorIndex: number = 1;
    sectionsLength: number = 0;
    haveSections: boolean = true;

    testState: State = new State({
        flow: null,
        sections: [],
        stateType: StateType.InputForm,
        key: -1
    });

    sections: Section[] = [];
    @Input() isResuming: boolean = false;
    @Input() state: State; // = this.testState;
    @Input() currentPage: Page;
	@Input() sectionContext: any;

	@Input() workflow: FlowInstance;

	private _settingsSubscription: Subscription;

	@Input() moduleConfig: ModuleConfig;
	@Input() expediteSetting: SystemSetting;

	get acceptingExpedites(): boolean {
		return this.expediteSetting != null && this.expediteSetting.isActive;
	}

	get expeditingMessage(): string {
		if (this.moduleConfig == null || this.moduleConfig.expediteFees == null) return "";
		return this.moduleConfig.expediteFees.expediteMessage;
	}

	get noExpeditingMessage(): string {
		if (this.moduleConfig == null || this.moduleConfig.expediteFees == null) return "";
		return this.moduleConfig.expediteFees.noExpediteMessage;
	}

	get showExpediteMessage(): boolean {
		return this.moduleConfig != null && this.moduleConfig.expediteFees != null && this.workflow != null && this.moduleConfig.expediteFees.flowsWithExpediting.findIndex(f => this.workflow.templateId.startsWith(f)) != -1;
	}

    @Output() navigateSection: EventEmitter<Section> = new EventEmitter<Section>();

    constructor(
        public el: ElementRef,
        public _eventsService: EventsService,
		public _changeDetectorRef: ChangeDetectorRef,
		private _config: ApplicationConfigService,
		private _settings: SystemSettingsService	) {
    }

	ngOnInit() {
		
    }

    ngAfterViewInit(): void {
        this.initializeView();
    }

    initializeSections(): void {
        if (!this.state || !this.state.sections) return;
        this.sections = this.state.sections;

        if (this.sections.length === 0) {
            console.log("Missing 'sections' in flow-sections.component.ts. Skipping indicator calc's");
            this.haveSections = false;
            return;
        }

        this.sectionsLength = this.sections.length;

        if (!this.currentPage) {
            this.currentPage = this.state.sections[0].pages[0] || null;
        };

        //window resize
        this._eventsService.create("flow-sections", window, "resize", () => this.onResize());
        this.initializeView();
    }

    initializeView(): void {
        if (this.haveSections) {
            //set size for single indicator
            this.setIndicatorWidth();
            //hide/show indicators based on width
            this.handleIndicatorDisplay();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.haveSections) {
            for (let propName in changes) {
                let change = changes[propName];
                //do we have a 'Back' or 'Next' click in the Workflow?
                if (change.currentValue instanceof State) {
                     this.initializeSections();
                } else if (change.currentValue instanceof Page) {
                    if (this.sectionsLength > 0) {
                        //update progress bar for currently viewing page
                        this.updateProgressBarForWorkflowProgression(change.currentValue.section);
                    }
                }
            }
        }
    }

    public hasSeenSection(section: string): boolean {
        return this.sectionContext.find(x => x === section);
    }

    ngOnDestroy(): void {
        if (this.haveSections) {
            this._eventsService.remove("flow-sections");
		}
		if (this._settingsSubscription != null) {
			this._settingsSubscription.unsubscribe();
		}
    }

    public clickSection(section: Section): void {
        this.navigateSection.emit(section);
    }

    onResize(): void {
        this.handleIndicatorDisplay();
    }
    /**
     * Main function to handle progress indicators, back & next
     */
    handleIndicatorDisplay(): void {
        this.setAvailableWidth();
        this.setHowManyIndicators();
        this.trimIndicators();
        this.setDisablePrevious();
        this.setDisableNext();
    }
    /**
     * Available width for all indicators
     */
    setAvailableWidth(): void {
        //container of available space for ALL progress elements
        let wrapper = <HTMLDivElement>this.el.nativeElement.querySelector(".progress-wrapper");
        //wrapper around '.progress-container'
        let progressContainer = <HTMLDivElement>this.el.nativeElement.querySelector(".progress-container");
        //a single button
        let backButton = <HTMLButtonElement>this.el.nativeElement.querySelector(".indicator-previous");
        if (!wrapper || !progressContainer || !backButton) return;
        //indicator area = wrapper - ('Progress: (1 of n)' + 'Back' + 'Next' buttons)
        this._availableWidth = Math.floor(wrapper.clientWidth) - (Math.ceil(progressContainer.clientWidth) + Math.ceil(backButton.clientWidth * 2));
    }
    /**
     * Width of a single indicator
     */
    setIndicatorWidth(): void {
        let indicator = <HTMLElement>this.el.nativeElement.querySelector("section-indicator");
        if (!indicator) return;
        //+10 = approximation due to pseudo's in DOM (using phone bp)
        this._indicatorWidth = Math.floor(indicator.offsetWidth) + 10;
    }
    /**
     * How many indicators can we fit in available space?
     */
    setHowManyIndicators(): void {
        for (let i in this.sections) {
            let indicatorIndex = parseInt(i) + 1;
            if (indicatorIndex * this._indicatorWidth <= this._availableWidth) {
                    this._showHowManyIndicators = indicatorIndex;
            }
        }
    }
    /**
     * Do we need to disable the 'Previous' button in the Progress Bar?
     */
    setDisablePrevious(): void {
        this.disablePrevious = this._aryCurrentlyVisibleIndicators[0] == 1;
        //alleviates 'ExpressionChangedAfterItHasBeenCheckedError'
        this._changeDetectorRef.detectChanges();
    }
    /**
     * Do we need to disable the 'Next' button in the Progress Bar?
     */
    setDisableNext(): void {
        this.disableNext = this._aryCurrentlyVisibleIndicators[this._aryCurrentlyVisibleIndicators.length - 1] == this.sectionsLength;
        //alleviates 'ExpressionChangedAfterItHasBeenCheckedError'
        this._changeDetectorRef.detectChanges();
    }
    /**
     * After WORKFLOW 'Back' or 'Next is clicked, sets that section indicator as first in progress bar
     * @param sectionName - name of the current section
 */
    updateProgressBarForWorkflowProgression(sectionId: string): void{
        this.currentEditingIndicatorIndex = this.sections.findIndex(x => x.id === sectionId) + 1;
        const diff = this.currentEditingIndicatorIndex - this._firstVisibleIndicator;
        this.incrementProgressBarVisibleIndicator(diff);
    }
    /**
        * Handles PROGRESS BAR click events from 'Next' & 'Previous' arrows
        * @param increment
    */
    incrementProgressBarVisibleIndicator(increment: number): void {
        let testFirstIncremented = this._firstVisibleIndicator + increment;
        //make sure the value isn't beyond available array
        if (testFirstIncremented >= 1 && testFirstIncremented <= this.sectionsLength) {
            this._firstVisibleIndicator = testFirstIncremented;
            this.trimIndicators();
            this.setDisablePrevious();
            this.setDisableNext();
        }
    }
    /**
        * Remove indicators that can't fit into available space
    */
    trimIndicators(): void {
        //clear previous array
        this._aryCurrentlyVisibleIndicators = [];
        const sections = this.sections;
        for (let i in sections) {
            if (sections.hasOwnProperty(i)) {
                const indicatorIndex = parseInt(i) + 1;
                const indicator = <HTMLDivElement>this.el.nativeElement.querySelector("#indicator" + indicatorIndex);
                if (!indicator) continue;
                if (indicatorIndex >= this._firstVisibleIndicator &&
                    indicatorIndex <= (this._firstVisibleIndicator - 1) + this._showHowManyIndicators) {
                    indicator.classList.remove("hiding");
                    //populate array of visible indicator indexes (staring at 1)
                    this._aryCurrentlyVisibleIndicators.push(indicatorIndex);
                } else {
                    indicator.classList.add("hiding");
                }
            }
        }
    }

}
